import { Component, OnInit } from '@angular/core';
import { FcmPermissionService } from '../services/fcm/fcm-permission.service';
import { FcmService } from '../services/fcm/fcm.service';
import { AuthService } from '../core/auth.service';

type BannerMode = 'ask' | 'help' | 'hidden';

@Component({
  selector: 'app-notification-permission-banner',
  templateUrl: './notification-permission-banner.component.html',
  styleUrls: ['./notification-permission-banner.component.css']
})
export class NotificationPermissionBannerComponent implements OnInit {
  visible = false;
  mode: BannerMode = 'hidden';
  busy = false;
  errorMsg: string = null;
  hintMsg: string = null;

  constructor(
    private perm: FcmPermissionService,
    private fcm: FcmService,
    private auth: AuthService
  ) {}

  async ngOnInit() {
    try { this.fcm.init(); await this.fcm.registerServiceWorker(); } catch {}

    this.setModeFromPermission();

    this.perm.onPermissionChange(async (state) => {
      if (state === 'prompt') {
        this.perm.unmarkPrompted();  // odblokuj baner
        this.mode = 'ask';
        this.visible = true;
        this.hintMsg = null;
        return;
      }
      if (state === 'denied') {
        this.perm.unmarkPrompted();
        this.mode = 'help';
        this.visible = true;
        this.hintMsg = this.buildWhyNoNotifications();
        return;
      }
      if (state === 'granted') {
        this.visible = false;
        this.mode = 'hidden';
        this.hintMsg = null;
      }
    });
  }

  private setModeFromPermission() {
    this.mode = this.perm.getBannerMode();
    this.visible = this.mode !== 'hidden';
    this.hintMsg = (this.mode === 'help') ? this.buildWhyNoNotifications() : null;
  }

  private buildWhyNoNotifications(): string {
    if (typeof Notification === 'undefined') { return 'Przeglądarka nie wspiera Notification API.'; }
    if (Notification.permission === 'denied') {
      return 'Powiadomienia są zablokowane dla tej witryny. W kłódce → Powiadomienia ustaw „Zezwalaj”, potem odśwież.';
    }
    if (Notification.permission === 'default') {
      // tslint:disable-next-line:max-line-length
      return 'Przeglądarka wyciszyła prośbę (quiet UI) lub jeszcze nie zdecydowałeś/aś. Kliknij ikonę dzwonka przy pasku adresu i wybierz „Zezwalaj”.';
    }
    if (location.protocol !== 'https:' && location.hostname !== 'localhost') {
      return 'Brak HTTPS (poza localhost). Włącz HTTPS, inaczej przeglądarka nie pozwoli na web push.';
    }
    if (!('serviceWorker' in navigator)) { return 'Brak wsparcia Service Worker w tej przeglądarce.'; }
    return 'Jeśli zgoda jest włączona, a nadal nie ma powiadomień: sprawdź rejestrację service workera i VAPID w konsoli (panel FCM).';
  }

  async enableNow() {
    if (this.mode !== 'ask') { return; }
    this.errorMsg = null;
    this.hintMsg = null;
    this.busy = true;

    try {
      const before = typeof Notification !== 'undefined' ? Notification.permission : 'unsupported';
      const quietTimer = setTimeout(() => {
        if (Notification.permission === 'default') {
          // tslint:disable-next-line:max-line-length
          this.hintMsg = 'Nie widzisz okna zgody? Przeglądarka mogła użyć cichego UI. Kliknij ikonę dzwonka przy pasku adresu i wybierz „Zezwalaj”.';
        }
      }, 3000);

      const result = await this.perm.hardRequestPermissionOnce();
      clearTimeout(quietTimer);

      if (result !== 'granted') {
        this.mode = (result === 'denied') ? 'help' : 'hidden';
        this.visible = (this.mode !== 'hidden');
        this.hintMsg = this.buildWhyNoNotifications();
        return;
      }

      if (!this.auth.getToken()) {
        const tok = await this.fcm.getToken();
        if (tok) {
          try { localStorage.setItem('fcmToken_pending', tok); localStorage.setItem('fcmToken', tok); } catch {}
        }
      }

      this.visible = false;
      this.mode = 'hidden';
      this.hintMsg = null;
    } catch {
      this.errorMsg = 'Nie udało się włączyć powiadomień.';
      this.visible = false;
      this.mode = 'hidden';
      this.hintMsg = null;
    } finally {
      this.busy = false;
    }
  }

  dismiss() {
    try { this.perm.markPromptedThisSession(); } catch {}
    this.visible = false;
  }
}
